import { useDataSentryContext } from '../context/DataSentryContext'
import { useTableFilters } from '../hooks/useTableFilters'
import { useDataSentryEventsByClassification } from 'api/dataSentry/queries/useDataSentryEventsByClassification'
import { ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { useRef, useMemo } from 'react'
import { DataGrid, theme, EmptyState, IconDictionary, Spinner } from '@ketch-com/deck'
import { dataSentryEventsByClassificationGridUtil } from './utils/DataSentryEventsByClassificationGridUtil'
import { SupportedClassificationTypes } from './types'
import { Box, Typography } from '@mui/material'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'

interface DataSentryDataClassificationEventsTableProps {
  classificationType: SupportedClassificationTypes
}

const titleCopyByClassificationType: Record<SupportedClassificationTypes, string> = {
  [ClassificationType.DATA_TYPE]: 'Data Types Collected',
  [ClassificationType.DATA_CATEGORY]: 'Data Categories Collected',
}

export const DataSentryDataClassificationEventsTable: React.FC<DataSentryDataClassificationEventsTableProps> = ({
  classificationType,
}) => {
  const { searchQuery, setSearchQuery, paginationModel, setPaginationModel } = useTableFilters(10)

  const {
    selectedTimeRange,
    selectedPropertyCode,
    eventsSummary,
    setDataClassificationDetails,
    hasPropertyAndScanTimeRange,
  } = useDataSentryContext()
  const { data: eventsByClassificationsList, isLoading } = useDataSentryEventsByClassification({
    params: {
      'classification-type': classificationType,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: searchQuery,
    },
    enabled: hasPropertyAndScanTimeRange,
  })
  const rowCountRef = useRef(eventsByClassificationsList?.total || 0)
  const rowCount = useMemo(() => {
    if (eventsByClassificationsList?.total !== undefined) {
      rowCountRef.current = eventsByClassificationsList.total
    }
    return rowCountRef.current
  }, [eventsByClassificationsList])

  const columns = useMemo(() => {
    return dataSentryEventsByClassificationGridUtil(
      { classificationType, totalCount: eventsSummary?.total },
      setDataClassificationDetails,
    )
  }, [classificationType, eventsSummary?.total, setDataClassificationDetails])

  const displayDataGridEmptyState = Boolean(!searchQuery && !eventsByClassificationsList?.classifications?.length)
  const displayDataGrid = Boolean(searchQuery || eventsByClassificationsList?.classifications?.length)

  return (
    <Box
      display="flex"
      width="49%"
      height="686px"
      flexDirection="column"
      gap={1}
      sx={{
        backgroundColor: theme.palette.Common.Container,
      }}
      borderRadius={3}
      px={2.5}
      py={2}
    >
      <Box display="flex" justifyContent="space-between" gap={3}>
        <Typography variant="h4">{titleCopyByClassificationType[classificationType]}</Typography>
        {displayDataGrid ? (
          <SearchTextInput
            onChange={nextValue => setSearchQuery(nextValue)}
            value={searchQuery}
            showClearButton
            minWidth="176px"
            sx={{
              width: '176px',
              '& .MuiInputBase-input': { maxWidth: '112px', minWidth: '112px' },
            }}
          />
        ) : null}
      </Box>
      <Box maxHeight="620px" height="620px">
        {isLoading && !displayDataGrid ? (
          <Box display="flex" alignItems="center" justifyContent="center" height={620}>
            <Spinner size="32px" thickness={2.5} />
          </Box>
        ) : null}
        {!isLoading && displayDataGridEmptyState ? (
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            iconName={IconDictionary.FCollection}
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        ) : null}
        {displayDataGrid ? (
          <DataGrid
            sx={{
              border: 0,
              paddingX: 0,
              maxHeight: '620px',
              height: '620px',
              '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
                backgroundImage: 'unset',
                cursor: 'auto',
              },
              ...(isLoading && {
                '& .MuiDataGrid-virtualScroller': {
                  height: '300px',
                },
              }),
            }}
            disableRowHoverStates
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnResize
            disableChildrenSorting={false}
            getRowHeight={() => 'auto'}
            getRowId={row => row.classification.code}
            rowCount={rowCount}
            rows={eventsByClassificationsList.classifications || []}
            columns={columns}
            pagination
            pageSizeOptions={[10]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={isLoading}
            paginationMode="server"
            noRowsOverlayProps={{ buttonTitle: '' }}
          />
        ) : null}
      </Box>
    </Box>
  )
}
